"use client";

import { useMemo } from "react";
import type { iCustomerPaymentMethod } from "@sdk/customer-payment-methods/customer-payment-method-model";
import type { iSupplierPaymentMethod } from "@sdk/supplier-payment-methods/supplier-payment-method-model";
import { useQueryWithStore } from "@/store/store.hooks";
import { selectCustomerPaymentMethodQuery } from "@/store/modules/customer-payment-methods/customer-payment-methods.selectors";
import { loadCustomerPaymentMethodsQuery } from "@/store/modules/customer-payment-methods/customer-payment-methods.helpers";
import { customerPaymentMethodsDefaultQueryConfig } from "@/store/modules/customer-payment-methods/customer-payment-methods-default-query";
import { selectSupplierPaymentMethodQuery } from "@/store/modules/supplier-payment-methods/supplier-payment-methods.selectors";
import { loadSupplierPaymentMethodsQuery } from "@/store/modules/supplier-payment-methods/supplier-payment-methods.helpers";
import { supplierPaymentMethodsDefaultQueryConfig } from "@/store/modules/supplier-payment-methods/supplier-payment-methods-default-query";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { isPaymentMethodAllowedForBrand } from "./payment-method-brand-filter";

type PaymentMethod = iCustomerPaymentMethod | iSupplierPaymentMethod;

export interface PaymentMethodSelectorProps {
  variant: "customer" | "supplier";
  value: string;
  onValueChange: (id: string) => void;
  placeholder?: string;
  label?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  hideLabel?: boolean;
  emptyHint?: string;
  /** When set, only payment methods with this default currency are selectable. */
  currencyFilter?: string;
  /** When set, payment methods with brand restrictions only appear if this brand is allowed. */
  brandIdFilter?: string;
}

function isActivePaymentMethod(pm: PaymentMethod): boolean {
  return pm.archived !== true;
}

export function PaymentMethodSelector({
  variant,
  value,
  onValueChange,
  placeholder,
  label = "Payment method",
  id,
  disabled,
  className,
  triggerClassName,
  hideLabel = false,
  emptyHint,
  currencyFilter,
  brandIdFilter,
}: PaymentMethodSelectorProps) {
  const normalizedCurrencyFilter = currencyFilter?.trim().toUpperCase() ?? "";
  const normalizedBrandIdFilter = brandIdFilter?.trim() ?? "";
  const customerQuery = useQueryWithStore(
    selectCustomerPaymentMethodQuery("all"),
    loadCustomerPaymentMethodsQuery(customerPaymentMethodsDefaultQueryConfig, "all"),
    [],
    variant !== "customer"
  );

  const supplierQuery = useQueryWithStore(
    selectSupplierPaymentMethodQuery("all"),
    loadSupplierPaymentMethodsQuery(supplierPaymentMethodsDefaultQueryConfig, "all"),
    [],
    variant !== "supplier"
  );

  const { isLoading, list } =
    variant === "customer"
      ? {
          isLoading: customerQuery.isLoading,
          list: (customerQuery.state?.list ?? []) as iCustomerPaymentMethod[],
        }
      : {
          isLoading: supplierQuery.isLoading,
          list: (supplierQuery.state?.list ?? []) as iSupplierPaymentMethod[],
        };

  const paymentMethods = useMemo(() => {
    let filtered = list.filter(isActivePaymentMethod);

    if (normalizedCurrencyFilter) {
      filtered = filtered.filter(
        (pm) =>
          pm.defaultCurrency?.trim().toUpperCase() === normalizedCurrencyFilter
      );
    }

    filtered = filtered.filter((pm) =>
      isPaymentMethodAllowedForBrand(pm, normalizedBrandIdFilter || undefined)
    );

    if (value) {
      const selected = list.find((pm) => pm.id === value);
      if (selected && !filtered.some((pm) => pm.id === selected.id)) {
        filtered = [...filtered, selected];
      }
    }
    return filtered
      .slice()
      .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
  }, [list, value, normalizedCurrencyFilter, normalizedBrandIdFilter]);

  const resolvedPlaceholder =
    placeholder ??
    (variant === "customer"
      ? "Select payment method"
      : "Select supplier payment method");

  const defaultEmptyHint =
    normalizedCurrencyFilter
      ? variant === "customer"
        ? `No customer payment methods in ${normalizedCurrencyFilter}. Create one in Workspace → Payment methods.`
        : `No supplier payment methods in ${normalizedCurrencyFilter}. Create one in Workspace → Payment methods.`
      : variant === "customer"
        ? "Configure customer payment methods in workspace settings first."
        : "Configure supplier payment methods in workspace settings first.";

  return (
    <div className={cn("grid gap-2", className)}>
      {!hideLabel && label ? <Label htmlFor={id}>{label}</Label> : null}
      <Select
        value={value || undefined}
        onValueChange={onValueChange}
        disabled={disabled || isLoading}
      >
        <SelectTrigger id={id} className={triggerClassName}>
          <SelectValue
            placeholder={isLoading ? "Loading…" : resolvedPlaceholder}
          />
        </SelectTrigger>
        <SelectContent>
          {paymentMethods.map((pm) =>
            pm.id ? (
              <SelectItem key={pm.id} value={pm.id}>
                {pm.label}
              </SelectItem>
            ) : null
          )}
        </SelectContent>
      </Select>
      {paymentMethods.length === 0 && !isLoading ? (
        <p className="text-xs text-muted-foreground">
          {emptyHint ?? defaultEmptyHint}
        </p>
      ) : null}
    </div>
  );
}
